import { AvatarFilter, AvatarFilterDeprecated } from '@/pages/avatar';
import { useAllAvatars } from '@/pages/avatar/hooks/useAllAvatars';
import { useFlag } from '@/services/featureFlags';
import { PresenterUsecase } from '@d-id/types';
import styled from '@emotion/styled';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useRef, useState } from 'react';

const TabsContainer = styled(Tabs)`
    display: flex;
    flex-wrap: wrap;
`;

const StyledTab = styled(Tab, { shouldForwardProp: prop => prop !== 'hide' && prop !== 'active' })<{ active?: boolean; hide?: boolean }>`
    display: ${({ hide }) => (hide ? 'none' : 'flex')};
    padding: 6px 16px;
    justify-content: center;
    align-items: center;
    min-height: 32px;
    margin-right: 6px;
    min-width: unset;

    border-radius: 8px;
    background: ${({ active, theme }) => (active ? theme.colors.secondary[900] : theme.colors.black[200])};

    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0.056px;
    text-transform: none;
    color: ${({ active, theme }) => (active ? theme.colors.white : theme.colors.secondary[700])} !important;

    &:hover {
        background: ${({ active, theme }) => (active ? theme.colors.secondary[700] : theme.colors.black[300])};
    }
`;

interface Props {
    filter: AvatarFilter | AvatarFilterDeprecated;
    setFilter: (filter: AvatarFilter | AvatarFilterDeprecated) => void;
    hideTab?: (tabs: AvatarFilterDeprecated) => boolean; // todo remove this props after flag new_avatars_filters will be enabled for everyone
}

export const AvatarTabs = ({ filter, setFilter, hideTab }: Props) => {
    const [avatarTab, setAvatarTab] = useState(0);
    const tabsRef = useRef<HTMLDivElement>(null);
    const { userAvatars } = useAllAvatars(PresenterUsecase.Agent);
    const newAvatarsFiltersEnabled = useFlag('new_avatars_filters', false);

    let scrollerRef: any;

    const scroll = (target: EventTarget & HTMLDivElement) => {
        if (!scrollerRef) {
            scrollerRef = tabsRef.current?.getElementsByClassName('MuiTabs-scroller')[0];
        }

        if (scrollerRef) {
            scrollerRef.scrollTo({ left: target.offsetLeft - target.offsetWidth, behavior: 'smooth' });
        }
    };

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        setAvatarTab(newValue);
    };

    const handleClick = (filterValue: AvatarFilter | AvatarFilterDeprecated) => (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setFilter(filterValue);
        scroll(e.currentTarget);
    };

    if (newAvatarsFiltersEnabled) {
        return (
            <TabsContainer
                value={avatarTab}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons={false}
                sx={{ '& .MuiTabs-indicator': { display: 'none' } }}
                ref={tabsRef}>
                <StyledTab label="All" active={filter === AvatarFilter.All} onClick={handleClick(AvatarFilter.All)} />
                <StyledTab
                    label="My Avatars"
                    hide={!userAvatars.length}
                    active={filter === AvatarFilter.MyAvatars}
                    onClick={handleClick(AvatarFilter.MyAvatars)}
                />
                <StyledTab
                    label="Video Avatars"
                    active={filter === AvatarFilter.VideoAvatars}
                    onClick={handleClick(AvatarFilter.VideoAvatars)}
                />
                <StyledTab
                    label="Photo Avatars"
                    active={filter === AvatarFilter.PhotoAvatars}
                    onClick={handleClick(AvatarFilter.PhotoAvatars)}
                />
            </TabsContainer>
        );
    }

    return (
        <TabsContainer
            value={avatarTab}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons={false}
            sx={{ '& .MuiTabs-indicator': { display: 'none' } }}
            ref={tabsRef}>
            {!hideTab?.(AvatarFilterDeprecated.All) && (
                <StyledTab label="All" active={filter === AvatarFilterDeprecated.All} onClick={handleClick(AvatarFilterDeprecated.All)} />
            )}
            {!hideTab?.(AvatarFilterDeprecated.MyAvatars) && (
                <StyledTab
                    label="My Avatars"
                    hide={!userAvatars.length}
                    active={filter === AvatarFilterDeprecated.MyAvatars}
                    onClick={handleClick(AvatarFilterDeprecated.MyAvatars)}
                />
            )}
            {!hideTab?.(AvatarFilterDeprecated.PremiumPlus) && (
                <StyledTab
                    label="Premium +"
                    active={filter === AvatarFilterDeprecated.PremiumPlus}
                    onClick={handleClick(AvatarFilterDeprecated.PremiumPlus)}
                />
            )}
            {!hideTab?.(AvatarFilterDeprecated.Premium) && (
                <StyledTab
                    label="Premium"
                    active={filter === AvatarFilterDeprecated.Premium}
                    onClick={handleClick(AvatarFilterDeprecated.Premium)}
                />
            )}
            {!hideTab?.(AvatarFilterDeprecated.Standard) && (
                <StyledTab
                    label="Standard"
                    active={filter === AvatarFilterDeprecated.Standard}
                    onClick={handleClick(AvatarFilterDeprecated.Standard)}
                />
            )}
        </TabsContainer>
    );
};
